import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  ApiResponse,
  ApiError,
  Book,
  BookImage,
  Tag,
  TagCategory,
  CreateBookData,
  UpdateBookData,
  CreateTagData,
  BookFilters,
  Exemplar,
  CreateExemplarData,
  UpdateExemplarData,
  ExemplarFilters,
  ImportStatus,
} from '../types';

// URL da API - detecta automaticamente o servidor
function getApiBaseUrl(): string {
  // Se tiver configuração explícita no .env, usa ela
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // Usa caminho relativo - funciona com Nginx reverse proxy
  // e tambem em desenvolvimento quando o Vite faz proxy
  return '/api';
}

// Configuração base da API
const API_BASE_URL = getApiBaseUrl();

// Instância do Axios configurada
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para tratamento de erros
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiResponse>) => {
    const apiError: ApiError = {
      code: 'UNKNOWN_ERROR',
      message: 'Ocorreu um erro inesperado. Tente novamente.',
    };

    // Sessão expirada (401 fora das rotas de /auth): limpa a sessão local e
    // recarrega para voltar à tela de login, em vez de deixar os formulários
    // falhando com erros genéricos
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/')) {
      try { localStorage.removeItem('admin_session'); } catch { /* noop */ }
      if (!sessionStorage.getItem('session_expired_reload')) {
        sessionStorage.setItem('session_expired_reload', '1');
        window.location.reload();
      }
      return Promise.reject({ code: 'SESSION_EXPIRED', message: 'Sua sessão expirou. Faça login novamente.' });
    }

    if (error.response?.data?.error) {
      return Promise.reject(error.response.data.error);
    }

    if (error.response) {
      switch (error.response.status) {
        case 400:
          apiError.code = 'BAD_REQUEST';
          apiError.message = 'Dados inválidos. Verifique os campos e tente novamente.';
          break;
        case 404:
          apiError.code = 'NOT_FOUND';
          apiError.message = 'Recurso não encontrado.';
          break;
        case 500:
          apiError.code = 'SERVER_ERROR';
          apiError.message = 'Erro interno do servidor. Tente novamente mais tarde.';
          break;
      }
    } else if (error.request) {
      apiError.code = 'NETWORK_ERROR';
      apiError.message = 'Erro de conexão. Verifique sua rede.';
    }

    return Promise.reject(apiError);
  }
);


// ============================================
// API de Autenticacao
// ============================================

// Usuário retornado pelo login/status (role vem na sessão — usado para
// restringir telas internas, ex.: estoque Mauá é só para admin)
export interface AuthUser {
  id: string;
  username: string;
  displayName?: string;
  role?: 'admin' | 'viewer';
}

export const authApi = {
  async login(username: string, password: string): Promise<{ user: AuthUser }> {
    const response = await apiClient.post<ApiResponse<{ user: AuthUser }>>('/auth/login', { username, password });
    if (!response.data.data) {
      throw response.data.error || { code: 'LOGIN_ERROR', message: 'Erro ao fazer login' };
    }
    // Login bem-sucedido: limpa a flag de reload por sessão expirada
    try { sessionStorage.removeItem('session_expired_reload'); } catch { /* noop */ }
    return response.data.data;
  },

  async logout(): Promise<void> {
    await apiClient.post('/auth/logout');
  },

  async getStatus(): Promise<{ isAuthenticated: boolean; user?: AuthUser }> {
    const response = await apiClient.get<ApiResponse<{ isAuthenticated: boolean; user?: AuthUser }>>('/auth/status');
    return response.data.data || { isAuthenticated: false };
  },
};
// ============================================
// API de Projetos
// ============================================

export const booksApi = {
  /**
   * Lista todos os projetos com filtros opcionais
   */
  async getAll(filters?: BookFilters): Promise<Book[]> {
    const params = new URLSearchParams();
    
    if (filters?.tags && filters.tags.length > 0) {
      filters.tags.forEach((tagId) => params.append('tags[]', tagId.toString()));
    }
    if (filters?.physicallyAvailable !== undefined) {
      params.append('physicallyAvailable', filters.physicallyAvailable.toString());
    }
    if (filters?.category !== undefined) {
      params.append('category', filters.category.toString());
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }

    const response = await apiClient.get<ApiResponse<Book[]>>('/books', { params });
    return response.data.data || [];
  },

  /**
   * Obtém um livro pelo ID
   */
  async getById(id: number): Promise<Book> {
    const response = await apiClient.get<ApiResponse<Book>>(`/books/${id}`);
    if (!response.data.data) {
      throw { code: 'NOT_FOUND', message: 'Projeto não encontrado.' };
    }
    return response.data.data;
  },

  /**
   * Cria um novo livro
   */
  async create(data: CreateBookData, images?: File[]): Promise<Book> {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('description', data.description);
    
    if (data.publicationYear) {
      formData.append('publicationYear', data.publicationYear.toString());
    }
    if (data.physicallyAvailable !== undefined) {
      formData.append('physicallyAvailable', data.physicallyAvailable.toString());
    }
    if (data.isAwarded !== undefined) {
      formData.append('isAwarded', data.isAwarded.toString());
    }
    if (data.boxType) {
      formData.append('boxType', data.boxType);
    }
    if (data.tagIds && data.tagIds.length > 0) {
      // Chave única com JSON: o multer não normaliza chaves `tagIds[]` em
      // req.body.tagIds (ficariam em req.body['tagIds[]']) e o backend só
      // entende a chave "tagIds" (array ou string JSON — ver parseTagIds).
      formData.append('tagIds', JSON.stringify(data.tagIds));
    }
    if (images) {
      images.forEach((image) => formData.append('images', image));
    }

    const response = await apiClient.post<ApiResponse<Book>>('/books', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    
    if (!response.data.data) {
      throw { code: 'CREATE_ERROR', message: 'Erro ao criar projeto.' };
    }
    return response.data.data;
  },

  /**
   * Atualiza um livro existente
   *
   * Campos "limpáveis": tagIds como [] remove todas as tags, boxType como ''
   * e publicationYear como null limpam esses campos — por isso os campos são
   * enviados sempre que definidos, mesmo "vazios" (o backend distingue
   * "ausente" de "vazio"). tagIds vai como JSON numa chave única porque o
   * multer não normaliza chaves com sufixo `[]`.
   */
  async update(id: number, data: UpdateBookData, newImages?: File[]): Promise<Book> {
    const formData = new FormData();

    if (data.title !== undefined) formData.append('title', data.title);
    if (data.description !== undefined) formData.append('description', data.description);
    if (data.publicationYear !== undefined) {
      // null = limpar o ano (backend entende string vazia como "sem ano")
      formData.append('publicationYear', data.publicationYear === null ? '' : data.publicationYear.toString());
    }
    if (data.physicallyAvailable !== undefined) {
      formData.append('physicallyAvailable', data.physicallyAvailable.toString());
    }
    if (data.isAwarded !== undefined) {
      formData.append('isAwarded', data.isAwarded.toString());
    }
    if (data.boxType !== undefined) {
      formData.append('boxType', data.boxType);
    }
    if (data.tagIds !== undefined) {
      formData.append('tagIds', JSON.stringify(data.tagIds));
    }
    if (newImages) {
      newImages.forEach((image) => formData.append('images', image));
    }

    const response = await apiClient.put<ApiResponse<Book>>(`/books/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    
    if (!response.data.data) {
      throw { code: 'UPDATE_ERROR', message: 'Erro ao atualizar projeto.' };
    }
    return response.data.data;
  },

  /**
   * Remove um livro
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/books/${id}`);
  },

  /**
   * Adiciona imagens a um livro existente
   */
  async addImages(bookId: number, images: File[]): Promise<BookImage[]> {
    const formData = new FormData();
    images.forEach((image) => formData.append('images', image));

    const response = await apiClient.post<ApiResponse<BookImage[]>>(
      `/books/${bookId}/images`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    
    return response.data.data || [];
  },

  /**
   * Remove uma imagem específica de um livro
   */
  async removeImage(bookId: number, imageId: number): Promise<void> {
    await apiClient.delete(`/books/${bookId}/images/${imageId}`);
  },

  /**
   * Reordena as imagens de um livro
   */
  async reorderImages(bookId: number, imageIds: number[]): Promise<void> {
    await apiClient.put(`/books/${bookId}/images/reorder`, { imageIds });
  },
};


// ============================================
// API de Exemplares em Estoque (Mauá)
// ============================================

export const exemplarsApi = {
  /**
   * Lista exemplares com filtros opcionais (busca por OP/título, local, projeto)
   */
  async getAll(filters?: ExemplarFilters): Promise<Exemplar[]> {
    const params = new URLSearchParams();

    if (filters?.search) {
      params.append('search', filters.search);
    }
    if (filters?.location) {
      params.append('location', filters.location);
    }
    if (filters?.bookId !== undefined) {
      params.append('bookId', filters.bookId.toString());
    }

    const response = await apiClient.get<ApiResponse<Exemplar[]>>('/exemplars', { params });
    return response.data.data || [];
  },

  /**
   * Obtém um exemplar pelo ID
   */
  async getById(id: number): Promise<Exemplar> {
    const response = await apiClient.get<ApiResponse<Exemplar>>(`/exemplars/${id}`);
    if (!response.data.data) {
      throw { code: 'NOT_FOUND', message: 'Exemplar não encontrado.' };
    }
    return response.data.data;
  },

  /**
   * Cria um novo exemplar (1 foto opcional no campo "photo")
   */
  async create(data: CreateExemplarData, photo?: File): Promise<Exemplar> {
    const formData = new FormData();
    formData.append('opNumber', data.opNumber);
    formData.append('title', data.title);
    formData.append('quantity', data.quantity.toString());

    if (data.bookId !== undefined && data.bookId !== null) {
      formData.append('bookId', data.bookId.toString());
    }
    if (data.location) {
      formData.append('location', data.location);
    }
    if (data.locationDetail) {
      formData.append('locationDetail', data.locationDetail);
    }
    if (data.notes) {
      formData.append('notes', data.notes);
    }
    if (photo) {
      formData.append('photo', photo);
    }

    const response = await apiClient.post<ApiResponse<Exemplar>>('/exemplars', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (!response.data.data) {
      throw { code: 'CREATE_ERROR', message: 'Erro ao criar exemplar.' };
    }
    return response.data.data;
  },

  /**
   * Atualiza um exemplar existente (substitui a foto se enviada)
   */
  async update(id: number, data: UpdateExemplarData, photo?: File): Promise<Exemplar> {
    const formData = new FormData();

    if (data.opNumber !== undefined) formData.append('opNumber', data.opNumber);
    if (data.title !== undefined) formData.append('title', data.title);
    if (data.quantity !== undefined) formData.append('quantity', data.quantity.toString());
    if (data.bookId !== undefined) {
      // String vazia desvincula o projeto (backend trata como null)
      formData.append('bookId', data.bookId === null ? '' : data.bookId.toString());
    }
    if (data.location !== undefined) formData.append('location', data.location);
    if (data.locationDetail !== undefined) formData.append('locationDetail', data.locationDetail);
    if (data.notes !== undefined) formData.append('notes', data.notes);
    if (photo) {
      formData.append('photo', photo);
    }

    const response = await apiClient.put<ApiResponse<Exemplar>>(`/exemplars/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (!response.data.data) {
      throw { code: 'UPDATE_ERROR', message: 'Erro ao atualizar exemplar.' };
    }
    return response.data.data;
  },

  /**
   * Remove um exemplar (e sua foto)
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/exemplars/${id}`);
  },
};


// ============================================
// API de Tags
// ============================================

export interface TagsByCategory {
  category: TagCategory;
  tags: Tag[];
}

export const tagsApi = {
  /**
   * Lista todas as tags organizadas por categoria
   */
  async getAll(): Promise<Tag[]> {
    const response = await apiClient.get<ApiResponse<Tag[]>>('/tags');
    return response.data.data || [];
  },

  /**
   * Lista todas as tags organizadas por categoria (com estrutura de categoria)
   */
  async getAllByCategory(): Promise<TagsByCategory[]> {
    const response = await apiClient.get<ApiResponse<TagsByCategory[]>>('/tags');
    return response.data.data || [];
  },

  /**
   * Lista todas as categorias de tags
   */
  async getCategories(): Promise<TagCategory[]> {
    const response = await apiClient.get<ApiResponse<TagCategory[]>>('/tags/categories');
    return response.data.data || [];
  },

  /**
   * Lista tags de uma categoria específica
   */
  async getByCategory(categoryId: number): Promise<Tag[]> {
    const response = await apiClient.get<ApiResponse<Tag[]>>(`/tags/category/${categoryId}`);
    return response.data.data || [];
  },

  /**
   * Cria uma nova tag
   */
  async create(data: CreateTagData): Promise<Tag> {
    const response = await apiClient.post<ApiResponse<Tag>>('/tags', data);
    if (!response.data.data) {
      throw { code: 'CREATE_ERROR', message: 'Erro ao criar tag.' };
    }
    return response.data.data;
  },

  /**
   * Remove uma tag
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/tags/${id}`);
  },

  /**
   * Move uma tag para outra categoria
   */
  async moveToCategory(tagId: number, newCategoryId: number): Promise<Tag> {
    const response = await apiClient.put<ApiResponse<Tag>>(`/tags/${tagId}/move`, {
      categoryId: newCategoryId,
    });
    if (!response.data.data) {
      throw { code: 'MOVE_ERROR', message: 'Erro ao mover tag.' };
    }
    return response.data.data;
  },

  /**
   * Obtém a contagem de livros que usam uma tag
   */
  async getBookCount(tagId: number): Promise<number> {
    const response = await apiClient.get<ApiResponse<{ tagId: number; bookCount: number }>>(`/tags/${tagId}/book-count`);
    return response.data.data?.bookCount || 0;
  },
};

// ============================================
// API de Importação WordPress
// ============================================

export const importApi = {
  /**
   * Inicia importação de arquivo XML do WordPress
   */
  async importWordPress(file: File): Promise<ImportStatus> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<ApiResponse<ImportStatus>>(
      '/import/wordpress',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    
    if (!response.data.data) {
      throw { code: 'IMPORT_ERROR', message: 'Erro ao iniciar importação.' };
    }
    return response.data.data;
  },

  /**
   * Verifica status de uma importação em andamento
   */
  async getStatus(importId: string): Promise<ImportStatus> {
    const response = await apiClient.get<ApiResponse<ImportStatus>>(`/import/status/${importId}`);
    if (!response.data.data) {
      throw { code: 'NOT_FOUND', message: 'Importação não encontrada.' };
    }
    return response.data.data;
  },
};

// ============================================
// API de Usuários
// ============================================

export interface UserData {
  id: number;
  username: string;
  displayName: string;
  role: 'admin' | 'viewer';
  isActive: boolean;
  createdAt?: string;
}

export const usersApi = {
  async getAll(): Promise<UserData[]> {
    const response = await apiClient.get<ApiResponse<UserData[]>>('/users');
    return response.data.data || [];
  },

  async create(data: { username: string; password: string; displayName?: string; role?: string }): Promise<UserData> {
    const response = await apiClient.post<ApiResponse<UserData>>('/users', data);
    if (!response.data.data) {
      throw { code: 'CREATE_ERROR', message: 'Erro ao criar usu\u00e1rio.' };
    }
    return response.data.data;
  },

  async update(id: number, data: { username?: string; password?: string; displayName?: string; role?: string; isActive?: boolean }): Promise<UserData> {
    const response = await apiClient.put<ApiResponse<UserData>>(`/users/${id}`, data);
    if (!response.data.data) {
      throw { code: 'UPDATE_ERROR', message: 'Erro ao atualizar usu\u00e1rio.' };
    }
    return response.data.data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  },
};

// ============================================
// Utilitários
// ============================================

/**
 * Gera URL completa para uma imagem
 */
export function getImageUrl(filepath: string): string {
  const baseUrl = getApiBaseUrl();
  // Remove /api do final se existir
  const serverUrl = baseUrl.replace(/\/api$/, '');
  return `${serverUrl}${filepath.startsWith('/') ? '' : '/'}${filepath}`;
}

/**
 * Gera URL para thumbnail de uma imagem
 *
 * O banco grava o caminho como 'uploads/arquivo.ext' (sem barra inicial) e o
 * thumbnail real fica em 'uploads/thumbnails/thumb_arquivo.ext' (prefixo thumb_),
 * então derivamos o caminho a partir do nome do arquivo.
 */
export function getThumbnailUrl(filepath: string): string {
  const baseUrl = getApiBaseUrl();
  const serverUrl = baseUrl.replace(/\/api$/, '');

  const filename = filepath.split('/').pop() || filepath;
  return `${serverUrl}/uploads/thumbnails/thumb_${filename}`;
}

// Export da instância do cliente para uso direto se necessário
export { apiClient };
